import { mutatorAction } from "satcheljs";
import { saveCommandingViewModeOption } from "owa-commanding-option";
import { isHostAppFeatureEnabled } from "owa-hostapp-feature-flags";
import { type CommandingViewMode } from "owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions";
import { OwsOptionsFeatureType } from "owa-outlook-service-option-store";
import { getStore } from "../store/store";
import { logUsage } from "owa-analytics";
import type { MailboxInfo } from "owa-client-types";

let isViewModeInitialized: boolean = false;

/**
 * Initializes owa-command-ribbon-store's viewMode using either OWS'
 * settings or the default values.
 */
export const initializeCommandingViewModeInternalMutator: (primeBootSettingsOptions: any[], mailboxInfo: MailboxInfo) => void = mutatorAction("initializeCommandingViewMode", (primeBootSettingsOptions: any[], mailboxInfo: MailboxInfo): void => {
    if (isViewModeInitialized) {
        return;
    }
    
    const preference = getDefaultCommandingViewModeOnBoot(primeBootSettingsOptions);
    const defaultPersistenceRequested = preference.viewMode === undefined && isHostAppFeatureEnabled("ribbonPersistDefault");
    const viewMode = validateViewMode(preference.viewMode, defaultPersistenceRequested);
    
    getStore().viewMode = viewMode;
    isViewModeInitialized = true;
    
    logUsage("InitializeCommandingViewMode", mailboxInfo, {
        viewMode,
        preferenceState: preference.preferenceState,
        selectionReason: preference.preferenceState === "valid"
            ? "persistedPreference"
            : preference.preferenceState === "missingSetting" ||
                preference.preferenceState === "missingValue"
                ? "defaultMissing"
                : "defaultInvalid",
        settingsSource: "bootSettings",
        // Boot settings do not include upstream read status; missing values do not prove a read failure.
        settingsReadOutcome: "unknown",
        defaultPersistenceRequested,
    });
});

/**
 * This function gets the default CommandingViewMode to render with
 * at boot. The value will either be from OWS' settings or the default values (in
 * the case where the user hasn't modified this preference yet, or the service
 * returns an unknown value).
 *
 * Note that we're getting these values from the UserConfiguration object instead
 * of from the "owa-outlook-service-options" package for boot bundle size health.
 */
const getDefaultCommandingViewModeOnBoot = (primeBootSettingsOptions: any[]): {
    viewMode: CommandingViewMode | undefined;
    preferenceState: "valid" | "missingSetting" | "missingValue" | "invalidType" | "unsupportedValue";
} => {
    const primeCommandingOptions: any[] = primeBootSettingsOptions.filter(item => item?.feature == OwsOptionsFeatureType.Commanding);
    
    const viewMode = primeCommandingOptions[0]?.viewMode;
    
    // Validate server is returning a client-supported value
    const validViewModes = [
        undefined,
        1,
        2,
    ];
    if (validViewModes.includes(viewMode)) {
        return {
            viewMode,
            preferenceState: primeCommandingOptions.length === 0
                ? "missingSetting"
                : viewMode === undefined
                    ? "missingValue"
                    : "valid",
        };
    }
    
    // viewMode is an unknown value or the wrong type. Returning
    // undefined ensures our default value logic will be used.
    return {
        viewMode: undefined,
        preferenceState: typeof viewMode === "number" ? "unsupportedValue" : "invalidType",
    };
};

/**
 * This function validates the viewMode, ensuring that the viewMode is in the correct state given the user's settings.
 * If it isn't, it will fix the state and persist the change.
 * @param viewMode The current viewMode that is not validity checked
 * @param defaultPersistenceRequested Whether to request saving the fallback mode when no valid preference exists
 */
const validateViewMode = (viewMode: CommandingViewMode | undefined, defaultPersistenceRequested: boolean): CommandingViewMode => {
    if (viewMode === undefined) {
        // No persisted viewMode, or invalid response from server. Fallback to client default
        viewMode = 1;
        
        if (defaultPersistenceRequested) {
            saveCommandingViewModeOption(viewMode, "defaultInitialization");
        }
    }
    
    return viewMode;
};
